import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import { UseResizableProps } from './hookProps';
import { UseResizableReturn } from './hookModel';
import { calculatePixelValue, checkNeedsResizeListener } from './hookHelper';

export function createUseResizableHook() {
    const useResizable = (props: UseResizableProps): UseResizableReturn => {
        const {
            width: { value: sidebarWidth, onChange: setSidebarWidth },
            minWidth: minWidthProp,
            maxWidth: maxWidthProp,
            initialWidth: initialWidthProp,
            direction = 'left',
        } = props;

        const [minPx, setMinPx] = useState(0);
        const [maxPx, setMaxPx] = useState(Infinity);
        const [isResizing, setIsResizing] = useState(false);

        // Get the current value based on breakpoint, casting to any to handle the type mismatch
        const currentMinWidth = useBreakpointValue(minWidthProp as any, {
            ssr: false,
        });
        const currentMaxWidth = useBreakpointValue(maxWidthProp as any, {
            ssr: false,
        });
        const currentInitialWidth = useBreakpointValue(initialWidthProp as any, {
            ssr: false,
        });

        // Calculate initial width px value from prop
        const initialWidthPx = calculatePixelValue(currentInitialWidth, 0);

        useEffect(() => {
            const updatePixelValues = () => {
                const newMinPx = calculatePixelValue(currentMinWidth, 0);
                const newMaxPx = calculatePixelValue(currentMaxWidth, Infinity);
                setMinPx(newMinPx);
                setMaxPx(newMaxPx);
            };

            updatePixelValues();

            const needsResizeListener =
                checkNeedsResizeListener(minWidthProp) ||
                checkNeedsResizeListener(maxWidthProp);

            if (needsResizeListener) {
                window.addEventListener('resize', updatePixelValues);
                return () => {
                    window.removeEventListener('resize', updatePixelValues);
                };
            }
            return undefined;
        }, [currentMinWidth, currentMaxWidth, minWidthProp, maxWidthProp]);

        const initialMouseX = useRef(0);
        const initialSidebarWidth = useRef(0);

        const handleMouseMove = useCallback(
            (event: MouseEvent) => {
                const deltaX = event.clientX - initialMouseX.current;
                // Invert deltaX for right-side panels
                const adjustedDelta = direction === 'right' ? -deltaX : deltaX;
                let newWidth = initialSidebarWidth.current + adjustedDelta;
                const clampedWidth = Math.max(minPx, Math.min(newWidth, maxPx));
                setSidebarWidth(clampedWidth);
            },
            [setSidebarWidth, minPx, maxPx, direction]
        );

        const handleMouseUp = useCallback(() => {
            setIsResizing(false);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }, [handleMouseMove]);

        const handleMouseDown = useCallback(
            (event: React.MouseEvent<HTMLDivElement>) => {
                event.preventDefault();
                setIsResizing(true);
                initialMouseX.current = event.clientX;
                const currentWidth = sidebarWidth ?? initialWidthPx;
                initialSidebarWidth.current = Math.max(
                    minPx,
                    Math.min(currentWidth, maxPx)
                );
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
                window.addEventListener('mousemove', handleMouseMove);
                window.addEventListener('mouseup', handleMouseUp);
            },
            [sidebarWidth, initialWidthPx, handleMouseMove, handleMouseUp, minPx, maxPx]
        );

        // Use initialWidthPx as the fallback if sidebarWidth state is null
        const displayWidth = sidebarWidth ?? initialWidthPx;

        return {
            layout: {
                width: `${displayWidth}px`,
            },
            resize: {
                isResizing,
                handle: {
                    onMouseDown: handleMouseDown,
                },
            },
        };
    };

    return useResizable;
}
